import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type ShoppingList } from '../api/endpoints';
import { ApiException } from '../api/client';
import { Skeleton, ErrorState } from '../components/states';
import { buildShoppingText, type ShoppingTextItem } from '../lib/shopping-text';
import { shareShoppingText, copyText } from '../lib/share';
import { clampServings } from '../lib/servings';

// «по вкусу» при отсутствии числа. Бэкенд уже нормализует amount=0 → null (shopping.ts),
// но трактуем и 0 здесь на случай иных источников — спека §6.
const fmtAmount = (amount: number | null, unit: string) =>
  amount === null || amount === 0 ? 'по вкусу' : `${amount} ${unit}`;

export function ShoppingListScreen() {
  const nav = useNavigate();
  const [data, setData] = useState<ShoppingList | null>(null);
  const [error, setError] = useState<string | null>(null);
  // checked[productId-unit] — есть дома (галочка стоит). Инициализируется из inPantry; меняется локально.
  // Ключ по product+unit (как ключ строк списка): один продукт в разных единицах = разные строки/галочки.
  // v1 не мутирует холодильник — это только локальное состояние экрана.
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<string | null>(null);
  const [servings, setServings] = useState<number | null>(null); // null = ещё определяем (грузим семью)
  const reqId = useRef(0);

  const load = useCallback(async (n: number) => {
    setError(null);
    const my = ++reqId.current; // защита от гонки: ответы устаревших запросов игнорируем
    try {
      const d = await endpoints.getShoppingList(n);
      if (my !== reqId.current) return;
      setData(d);
      setChecked(Object.fromEntries(d.items.map((i) => [`${i.productId}-${i.unit}`, i.inPantry])));
    } catch (e) {
      if (my !== reqId.current) return;
      setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить список');
    }
  }, []);

  useEffect(() => {
    // Дефолт N = число членов семьи (если профилей нет — 4), затем грузим список.
    // setState живёт в .then (вне тела эффекта) — правило set-state-in-effect не срабатывает.
    let alive = true;
    void endpoints
      .getFamily()
      .then((f) => (f.length >= 1 ? f.length : 4))
      .catch(() => 4)
      .then((defN) => {
        if (!alive) return;
        setServings(defN);
        return load(defN);
      });
    return () => {
      alive = false;
    };
  }, [load]);

  function changeServings(delta: number) {
    const next = clampServings((servings ?? 4) + delta);
    setServings(next);
    void load(next);
  }

  if (data === null && !error)
    return (
      <div className="space-y-3 px-4 pt-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  if (error)
    return (
      <div className="px-4 pt-6">
        <ErrorState message={error} onRetry={() => void load(servings ?? 4)} />
      </div>
    );

  // «Не хватает» = строки с снятой галочкой (нет дома). Считается по локальному checked,
  // поэтому снятие галочки тут же переносит продукт в этот блок.
  const missing = data!.items.filter((i) => !checked[`${i.productId}-${i.unit}`]);

  // Текст для отправки: недостающее, сгруппированное по отделам, + extras. Пустой → '' (кнопки скрыты).
  const missingForText: ShoppingTextItem[] = missing.map((i) => ({
    name: i.name,
    unit: i.unit,
    amount: i.amount,
    category: i.category,
  }));
  const listText = buildShoppingText(missingForText, data!.extras);
  const onShare = async () => {
    const r = await shareShoppingText(listText);
    if (r === 'copied') setToast('Список скопирован');
    else if (r === 'failed') setToast('Не получилось — попробуйте «Скопировать»');
  };
  const onCopy = async () => setToast((await copyText(listText)) ? 'Список скопирован' : 'Не удалось скопировать');

  return (
    <div className="px-4 pt-6">
      <button onClick={() => nav('/plan')} className="mb-3 text-sm text-primary-700">
        ← Меню недели
      </button>
      <h1 className="mb-4 text-2xl font-bold text-ink">Список покупок</h1>

      {data && (data.items.length > 0 || data.extras.length > 0) && (
        <div className="mb-4 flex items-center gap-3">
          <span className="text-sm text-ink/70">На сколько человек:</span>
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Меньше порций"
              onClick={() => changeServings(-1)}
              className="h-8 w-8 rounded-full bg-white text-lg leading-none text-primary-700 shadow-sm"
            >
              −
            </button>
            <span aria-label="Число человек" className="w-6 text-center font-semibold text-ink">
              {servings ?? 4}
            </span>
            <button
              type="button"
              aria-label="Больше порций"
              onClick={() => changeServings(1)}
              className="h-8 w-8 rounded-full bg-white text-lg leading-none text-primary-700 shadow-sm"
            >
              +
            </button>
          </div>
        </div>
      )}

      {data!.items.length === 0 && data!.extras.length === 0 && (
        <p className="text-ink/60">Сначала соберите меню недели — список появится здесь.</p>
      )}

      {data!.items.length > 0 && (
        <>
          <h2 className="mb-2 text-sm font-semibold text-ink/60">Продукты (отметьте, что уже есть)</h2>
          <ul className="mb-6 space-y-1.5">
            {data!.items.map((i) => (
              <li key={`${i.productId}-${i.unit}`}>
                <label className="flex items-center gap-3 rounded-xl bg-white px-3 py-2 shadow-sm">
                  <input
                    type="checkbox"
                    checked={!!checked[`${i.productId}-${i.unit}`]}
                    aria-label={i.name}
                    onChange={(e) => setChecked((c) => ({ ...c, [`${i.productId}-${i.unit}`]: e.target.checked }))}
                  />
                  <span className="flex-1 text-sm text-ink">{i.name}</span>
                  <span className="text-sm text-ink/50">{fmtAmount(i.amount, i.unit)}</span>
                </label>
              </li>
            ))}
          </ul>
        </>
      )}

      <h2 className="mb-2 text-sm font-semibold text-ink/60">Не хватает — купить</h2>
      <ul data-testid="missing-list" className="space-y-1.5">
        {missing.length === 0 ? (
          <li className="text-ink/50">Всё есть дома 🎉</li>
        ) : (
          missing.map((i) => (
            <li
              key={`${i.productId}-${i.unit}`}
              className="flex justify-between rounded-xl bg-primary-50 px-3 py-2 text-sm text-primary-800"
            >
              <span>{i.name}</span>
              <span>{fmtAmount(i.amount, i.unit)}</span>
            </li>
          ))
        )}
      </ul>

      {listText && (
        <div className="mt-4 flex gap-2">
          <button
            onClick={() => void onShare()}
            className="flex-1 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white"
          >
            Отправить список
          </button>
          <button
            onClick={() => void onCopy()}
            className="rounded-xl bg-white px-4 py-3 text-sm font-semibold text-primary-700 shadow-sm"
          >
            Скопировать
          </button>
        </div>
      )}
      {toast && <p role="status" className="mt-2 text-center text-sm text-ink/60">{toast}</p>}

      {data!.extras.length > 0 && (
        <>
          <h2 className="mb-2 mt-6 text-sm font-semibold text-ink/60">По вкусу / без количества</h2>
          <ul className="space-y-1 text-sm text-ink/60">
            {data!.extras.map((x, idx) => (
              <li key={idx}>{x.name}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
